import React, { useEffect, useState } from 'react';
import api, { isStaffPlus, getCurrentUser } from '../api/expoApi';
import HubSelector from '../components/HubSelector';

export default function InventoryPage() {
    const user = getCurrentUser();
    const canEdit = isStaffPlus(user?.role);
    const [tab, setTab] = useState('products');
    const [products, setProducts] = useState([]);
    const [dailyStock, setDailyStock] = useState([]);
    const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
    const [newProduct, setNewProduct] = useState({
        sku: '', name: '', category: '', unit: 'EACH', is_gift_service: false,
        unit_cost: '', sell_price: '', low_stock_threshold: 10
    });
    const [movement, setMovement] = useState({ product_id: '', movement_type: 'INWARD', quantity: '', notes: '' });
    const [msg, setMsg] = useState('');

    const load = async () => {
        try {
            const [p, ds] = await Promise.all([
                api.get('/inventory/products'),
                api.get('/inventory/daily-stock', { params: { date } })
            ]);
            setProducts(p.data?.data || []);
            setDailyStock(ds.data?.data || []);
        } catch (e) { console.error(e); }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [date]);

    const createProduct = async () => {
        try {
            await api.post('/inventory/products', newProduct);
            setMsg('Product created');
            setNewProduct({ sku: '', name: '', category: '', unit: 'EACH', is_gift_service: false,
                unit_cost: '', sell_price: '', low_stock_threshold: 10 });
            load();
        } catch (e) { setMsg(e.response?.data?.message || 'Error'); }
    };

    const recordMovement = async () => {
        try {
            await api.post('/inventory/movements', movement);
            setMsg('Movement recorded');
            setMovement({ product_id: '', movement_type: 'INWARD', quantity: '', notes: '' });
            load();
        } catch (e) { setMsg(e.response?.data?.message || 'Error'); }
    };

    return (
        <div style={{ padding: 24 }}>
            <h1>Inventory & Gift Services</h1>
            {msg && <div style={{ background: '#d1e7dd', padding: 8, borderRadius: 4, marginBottom: 12 }}>{msg}</div>}

            <div style={{ borderBottom: '1px solid #ddd', marginBottom: 16 }}>
                {['products', 'daily-stock', 'movements'].map((t) => (
                    <button key={t} onClick={() => setTab(t)}
                        style={{
                            border: 'none', padding: '10px 16px',
                            background: tab === t ? '#fff' : 'transparent',
                            borderBottom: tab === t ? '2px solid #0d6efd' : 'none',
                            cursor: 'pointer', textTransform: 'capitalize'
                        }}>
                        {t.replace('-', ' ')}
                    </button>
                ))}
            </div>

            {tab === 'products' && (
                <>
                    {canEdit && (
                        <div style={{ background: '#f8f9fa', padding: 16, borderRadius: 6, marginBottom: 16 }}>
                            <h3>Add Product</h3>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                                <input placeholder="SKU" value={newProduct.sku} onChange={(e) => setNewProduct({...newProduct, sku: e.target.value})} />
                                <input placeholder="Name" value={newProduct.name} onChange={(e) => setNewProduct({...newProduct, name: e.target.value})} />
                                <input placeholder="Category (e.g. PACKING, GIFT_FLOWERS)" value={newProduct.category} onChange={(e) => setNewProduct({...newProduct, category: e.target.value.toUpperCase()})} />
                                <input placeholder="Unit (EACH, BOX, KG)" value={newProduct.unit} onChange={(e) => setNewProduct({...newProduct, unit: e.target.value})} />
                                <input type="number" step="0.01" placeholder="Unit cost" value={newProduct.unit_cost} onChange={(e) => setNewProduct({...newProduct, unit_cost: e.target.value})} />
                                <input type="number" step="0.01" placeholder="Sell price" value={newProduct.sell_price} onChange={(e) => setNewProduct({...newProduct, sell_price: e.target.value})} />
                                <input type="number" placeholder="Low stock threshold" value={newProduct.low_stock_threshold} onChange={(e) => setNewProduct({...newProduct, low_stock_threshold: e.target.value})} />
                                <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <input type="checkbox" checked={newProduct.is_gift_service} onChange={(e) => setNewProduct({...newProduct, is_gift_service: e.target.checked})} />
                                    Gift Service
                                </label>
                            </div>
                            <button onClick={createProduct} style={{ marginTop: 8, padding: '8px 16px' }}>Create</button>
                        </div>
                    )}
                    <table style={tableStyle}>
                        <thead><tr><th>SKU</th><th>Name</th><th>Category</th><th>Type</th><th>Current Stock</th><th>Threshold</th><th>Cost</th><th>Price</th></tr></thead>
                        <tbody>
                            {products.map((p) => (
                                <tr key={p.product_id} style={Number(p.current_qty) <= p.low_stock_threshold ? { background: '#fff3cd' } : {}}>
                                    <td><code>{p.sku}</code></td>
                                    <td>{p.name}</td>
                                    <td>{p.category}</td>
                                    <td>{p.is_gift_service ? '🎁 Gift' : 'Stock'}</td>
                                    <td style={{ fontWeight: 600 }}>{p.current_qty}</td>
                                    <td>{p.low_stock_threshold}</td>
                                    <td>{p.unit_cost ? `PKR ${p.unit_cost}` : '—'}</td>
                                    <td>{p.sell_price ? `PKR ${p.sell_price}` : '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </>
            )}

            {tab === 'daily-stock' && (
                <>
                    <div style={{ marginBottom: 12 }}>
                        Date: <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
                    </div>
                    <table style={tableStyle}>
                        <thead><tr><th>SKU</th><th>Product</th><th>Category</th><th>Opening</th><th>+IN</th><th>−OUT</th><th>Closing</th></tr></thead>
                        <tbody>
                            {dailyStock.map((s) => (
                                <tr key={s.product_id}>
                                    <td><code>{s.sku}</code></td>
                                    <td>{s.name}</td>
                                    <td>{s.category}</td>
                                    <td>{s.opening_qty}</td>
                                    <td style={{ color: '#198754' }}>+{s.inward_qty}</td>
                                    <td style={{ color: '#dc3545' }}>−{s.outward_qty}</td>
                                    <td style={{ fontWeight: 600 }}>{s.closing_qty}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </>
            )}

            {tab === 'movements' && canEdit && (
                <div style={{ background: '#f8f9fa', padding: 16, borderRadius: 6 }}>
                    <h3>Record Stock Movement</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                        <select value={movement.product_id} onChange={(e) => setMovement({...movement, product_id: e.target.value})}>
                            <option value="">Select product...</option>
                            {products.map((p) => <option key={p.product_id} value={p.product_id}>{p.sku} — {p.name}</option>)}
                        </select>
                        <select value={movement.movement_type} onChange={(e) => setMovement({...movement, movement_type: e.target.value})}>
                            <option value="INWARD">Inward (+)</option>
                            <option value="OUTWARD">Outward (−)</option>
                            <option value="ADJUSTMENT">Adjustment</option>
                            <option value="OPENING">Opening Balance</option>
                        </select>
                        <input type="number" placeholder="Quantity" value={movement.quantity} onChange={(e) => setMovement({...movement, quantity: e.target.value})} />
                        <input placeholder="Notes" value={movement.notes} onChange={(e) => setMovement({...movement, notes: e.target.value})} />
                    </div>
                    <button onClick={recordMovement} style={{ marginTop: 8, padding: '8px 16px' }}>Record</button>
                </div>
            )}
        </div>
    );
}

const tableStyle = { width: '100%', borderCollapse: 'collapse', background: '#fff' };
